import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Database, TablesInsert } from '@pirouette/db';
import { supabase } from '@/lib/supabase';

// Staff Operations data layer.
//
// Mirrors @/data/bi.ts and @/data/instructors.ts: thin, typed react-query
// wrappers over `supabase.from(...)` / `supabase.rpc(...)`. Every row type is
// derived from the generated `@pirouette/db` Database types so the UI tracks
// the database contract. Nothing here bypasses RLS — each call runs as the
// authenticated user; the per-table policies (admin full; faculty self-manage
// own availability + own time_clock) scope what's visible/writable. The
// `studio_id` column is filled server-side by a default, so inserts omit it
// (same approach instructors.ts / classes use).

type Row<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
type RpcReturns<K extends keyof Database['public']['Functions']> =
  Database['public']['Functions'][K]['Returns'];

export type StaffAvailabilityRow = Row<'staff_availability'>;
export type TimeClockEntryRow = Row<'time_clock_entries'>;
export type ClassSubstitutionRow = Row<'class_substitutions'>;

/** One payroll row from staff_payroll_export(p_start, p_end). */
export type StaffPayrollRow = RpcReturns<'staff_payroll_export'>[number];

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Payroll export for a date range: hours + entry counts per instructor. */
export function useStaffPayroll(start: string, end: string) {
  return useQuery({
    // Only run once both bounds are set — the RPC requires non-null dates.
    enabled: Boolean(start && end),
    queryKey: ['staff', 'payroll', start, end],
    queryFn: async (): Promise<StaffPayrollRow[]> => {
      const { data, error } = await supabase.rpc('staff_payroll_export', {
        p_start: start,
        p_end: end,
      });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Weekly availability blocks for one instructor (ordered by weekday + start). */
export function useInstructorAvailability(instructorId: string | undefined) {
  return useQuery({
    enabled: Boolean(instructorId),
    queryKey: ['staff', 'availability', instructorId],
    queryFn: async (): Promise<StaffAvailabilityRow[]> => {
      const { data, error } = await supabase
        .from('staff_availability')
        .select(
          'id, instructor_id, weekday, start_time, end_time, available, notes, studio_id, created_at, updated_at',
        )
        .eq('instructor_id', instructorId!)
        .order('weekday', { ascending: true })
        .order('start_time', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Recent time-clock entries for one instructor (most recent first). */
export function useTimeClock(instructorId: string | undefined, limit = 20) {
  return useQuery({
    enabled: Boolean(instructorId),
    queryKey: ['staff', 'time-clock', instructorId, limit],
    queryFn: async (): Promise<TimeClockEntryRow[]> => {
      const { data, error } = await supabase
        .from('time_clock_entries')
        .select('id, instructor_id, clock_in, clock_out, notes, studio_id, created_at, updated_at')
        .eq('instructor_id', instructorId!)
        .order('clock_in', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Pending + recent substitution requests (most recent first). */
export function useClassSubstitutions(limit = 50) {
  return useQuery({
    queryKey: ['staff', 'substitutions', limit],
    queryFn: async (): Promise<ClassSubstitutionRow[]> => {
      const { data, error } = await supabase
        .from('class_substitutions')
        .select(
          'id, class_session_id, substitute_instructor_id, reason, status, studio_id, created_at, updated_at',
        )
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface AvailabilityInput {
  instructor_id: string;
  weekday: string;
  start_time: string;
  end_time: string;
  available?: boolean;
  notes?: string | null;
}

/** Add a weekly availability block for an instructor. */
export function useAddAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AvailabilityInput): Promise<StaffAvailabilityRow> => {
      const payload: TablesInsert<'staff_availability'> = input;
      const { data, error } = await supabase
        .from('staff_availability')
        .insert(payload)
        .select(
          'id, instructor_id, weekday, start_time, end_time, available, notes, studio_id, created_at, updated_at',
        )
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['staff', 'availability', vars.instructor_id] });
    },
  });
}

/** Clock an instructor in — inserts a row with clock_in = now and no clock_out. */
export function useClockIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      instructorId: string;
      notes?: string | null;
    }): Promise<TimeClockEntryRow> => {
      const payload: TablesInsert<'time_clock_entries'> = {
        instructor_id: args.instructorId,
        clock_in: new Date().toISOString(),
        notes: args.notes ?? null,
      };
      const { data, error } = await supabase
        .from('time_clock_entries')
        .insert(payload)
        .select('id, instructor_id, clock_in, clock_out, notes, studio_id, created_at, updated_at')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['staff', 'time-clock', vars.instructorId] });
    },
  });
}

/** Clock out an open entry — stamps clock_out = now on the given entry id. */
export function useClockOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      entryId: string;
      instructorId: string;
    }): Promise<TimeClockEntryRow> => {
      const { data, error } = await supabase
        .from('time_clock_entries')
        .update({ clock_out: new Date().toISOString() })
        .eq('id', args.entryId)
        .select('id, instructor_id, clock_in, clock_out, notes, studio_id, created_at, updated_at')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['staff', 'time-clock', vars.instructorId] });
    },
  });
}

export interface SubstitutionInput {
  class_session_id: string;
  substitute_instructor_id: string;
  reason?: string | null;
}

/** Request a substitution for a class session (status starts 'requested'). */
export function useRequestSubstitution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SubstitutionInput): Promise<ClassSubstitutionRow> => {
      const payload: TablesInsert<'class_substitutions'> = {
        class_session_id: input.class_session_id,
        substitute_instructor_id: input.substitute_instructor_id,
        reason: input.reason ?? null,
        status: 'requested',
      };
      const { data, error } = await supabase
        .from('class_substitutions')
        .insert(payload)
        .select(
          'id, class_session_id, substitute_instructor_id, reason, status, studio_id, created_at, updated_at',
        )
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['staff', 'substitutions'] });
    },
  });
}
